import { z } from 'zod';
/**
 * A categoria do produto — F163, migração 0092.
 *
 * Vocabulário controlado, no molde do cadastro de marcas (`brand.ts`), com uma
 * diferença que vem do banco: o produto aponta para a categoria por `id`, e não
 * por nome. Renomear a categoria, por isso, é um `update` numa linha só — não
 * existe aqui o problema que fez `renameBrand` virar uma transação de quatro
 * tabelas.
 *
 * A lista nasce vazia. A loja cria Tela e Bateria; outra loja cria o que ela
 * vende.
 */
export const ProductCategoryStatusSchema = z.enum(['active', 'archived']);
export const ProductCategorySummary = z.object({
    id: z.string(),
    name: z.string(),
    /** A categoria de cima (F243): nula numa de cima. Um nível só. */
    parentId: z.string().nullable().default(null),
    parentName: z.string().nullable().default(null),
    status: ProductCategoryStatusSchema,
    /**
     * Quantos produtos estão nesta categoria hoje.
     *
     * Pela mesma razão do cadastro de marcas: arquivar uma categoria com 1.205
     * telas é uma decisão diferente de arquivar uma que ninguém usa.
     */
    usageCount: z.number().int(),
    version: z.number().int(),
});
export const ProductCategoryListQuery = z.object({
    status: ProductCategoryStatusSchema.optional(),
});
export const ProductCategoryListResponse = z.object({
    categories: z.array(ProductCategorySummary),
});
export const CreateProductCategoryRequest = z.object({
    name: z
        .string()
        .trim()
        .min(1, 'Dê um nome à categoria.')
        .max(120, 'Use no máximo 120 caracteres.'),
    /** Para criar uma subcategoria (F243). */
    parentId: z.uuid().nullable().optional(),
});
/** Arquivar e reativar pela mesma rota. Os produtos não são tocados. */
export const SetProductCategoryStatusRequest = z.object({
    status: ProductCategoryStatusSchema,
    version: z.number().int(),
});
/**
 * Renomear: um `update` só, porque o produto aponta por `id`.
 */
export const RenameProductCategoryRequest = z.object({
    name: CreateProductCategoryRequest.shape.name,
    version: z.number().int(),
});
/**
 * Marcar de uma vez os produtos que já existem, pelo começo do nome (F163).
 *
 * Na Elite Digital o nome carrega a família desde o sistema antigo: `f.` é
 * tela, `bat.` é bateria. Em vez de 1.205 cliques, a loja descreve as regras e
 * vê a prévia — `dryRun` — antes de gravar.
 *
 * Só toca em produto SEM categoria: a marcação feita à mão vale mais que uma
 * regra de prefixo, e uma segunda passada não pode desfazer a correção que
 * alguém fez ontem.
 */
/**
 * Uma regra do "Classificar pelo nome" (F272): o texto, se o nome começa com
 * ele ou o contém, a categoria, e se troca também quem já tem outra.
 *
 * `prefix` é o nome antigo de `pattern` (F163): um cliente desatualizado ainda
 * manda assim, e continua funcionando.
 */
export const CATEGORIZE_MATCHES = ['prefix', 'contains'];
export const CategorizeRuleSchema = z.preprocess((value) => typeof value === 'object' &&
    value !== null &&
    !('pattern' in value) &&
    'prefix' in value
    ? { ...value, pattern: value.prefix }
    : value, z.object({
    pattern: z.string().trim().min(1, 'Escreva o texto do nome.').max(60),
    match: z.enum(CATEGORIZE_MATCHES).default('prefix'),
    categoryId: z.string().min(1),
    overwrite: z.boolean().default(false),
}));
export const CategorizeByNameRequest = z.object({
    rules: z.array(CategorizeRuleSchema).min(1).max(30),
    /** Só conta, não grava. É o que a tela chama de prévia. */
    dryRun: z.boolean().default(false),
});
export const CategorizeByNameResponse = z.object({
    rules: z.array(z.object({
        pattern: z.string(),
        match: z.enum(CATEGORIZE_MATCHES),
        overwrite: z.boolean(),
        categoryId: z.string(),
        categoryName: z.string(),
        /** Quantos produtos a regra marca: os sem categoria e os que trocam. */
        matched: z.number().int().nonnegative(),
        /** Desses, quantos já estavam noutra categoria (só com `overwrite`). */
        changed: z.number().int().nonnegative(),
    })),
    /** Quantos produtos continuam sem categoria depois destas regras. */
    remaining: z.number().int().nonnegative(),
    /** Quantos foram gravados — zero na prévia. */
    updated: z.number().int().nonnegative(),
});
/** As regras gravadas da conta, na ordem (F272). */
export const NameRulesResponse = z.object({
    rules: z.array(z.object({
        pattern: z.string(),
        match: z.enum(CATEGORIZE_MATCHES),
        categoryId: z.string(),
        overwrite: z.boolean(),
    })),
});
export const SaveNameRulesRequest = z.object({
    rules: z.array(CategorizeRuleSchema).max(30),
});
/**
 * As categorias por planilha (F243): `codigo;categoria;subcategoria`.
 *
 * Só preenche produto SEM categoria, e cria a categoria e a subcategoria que
 * faltam. `dryRun` é a prévia: o mesmo caminho, sem gravar.
 */
export const ImportProductCategoriesRequest = z.object({
    rows: z
        .array(z.object({
        codigo: z.string().trim().min(1).max(60),
        categoria: z.string().trim().min(1).max(120),
        subcategoria: z.string().trim().max(120).nullable().default(null),
    }))
        .min(1, 'A planilha não tem nenhuma linha.')
        .max(10_000, 'No máximo 10.000 linhas por vez.'),
    dryRun: z.boolean().default(false),
});
export const ImportProductCategoriesResponse = z.object({
    categoriesCreated: z.array(z.string()),
    subcategoriesCreated: z.array(z.string()),
    marked: z.number().int().nonnegative(),
    alreadyCategorized: z.number().int().nonnegative(),
    unknownCodes: z.array(z.string()),
    unknownCount: z.number().int().nonnegative(),
    applied: z.boolean(),
});
/**
 * O estoque mínimo de uma categoria inteira de uma vez (F288).
 *
 * "Tela é 5": o Eduardo dita a regra por categoria, e a prévia (`dryRun`)
 * conta quantas peças recebem. Sem `overwrite`, só quem ainda não tem mínimo
 * — o que alguém acertou à mão numa peça continua valendo. As subcategorias
 * entram junto.
 */
export const MinimumStockByCategoryRequest = z.object({
    categoryId: z.uuid(),
    minimum: z
        .string()
        .trim()
        .regex(/^\d{1,6}$/, 'Informe o mínimo em unidades inteiras.'),
    overwrite: z.boolean().default(false),
    dryRun: z.boolean().default(true),
});
export const MinimumStockByCategoryResponse = z.object({
    /** Peças (variações ativas) que recebem o mínimo. */
    matched: z.number().int().nonnegative(),
    /** Gravadas — zero na prévia. */
    updated: z.number().int().nonnegative(),
});
//# sourceMappingURL=product-category.js.map